/**
 * 可灵对口型节点 - 音频对口型
 * 
 * 功能说明：
 * 使用音频文件为视频配音，实现口型同步
 * 
 * 连接说明：
 * - 输入：视频任务节点或视频参考节点
 * - 输入：音频参考节点
 * - 输出：对口型任务节点
 */
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './KlingLipSyncNode.css';

const apiService = new ApiService();

export const KlingLipSyncNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const { readOnly } = useProject();
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // 获取连接的视频节点
  const getConnectedVideoUrl = useCallback(() => {
    // 查找连接到 video handle 的边，如果没有 targetHandle 则查找所有连接的边
    const connectedEdges = edges.filter(edge => {
      if (edge.target !== id) return false;
      // 如果有 targetHandle，必须是 'video'；如果没有 targetHandle，则检查源节点类型
      if (edge.targetHandle) {
        return edge.targetHandle === 'video';
      }
      // 没有 targetHandle 的情况下，只接受 videoTask 和 videoReference 类型
      const sourceNode = getNode(edge.source);
      return sourceNode && (sourceNode.type === 'videoTask' || sourceNode.type === 'videoReference');
    });
    
    if (connectedEdges.length === 0) return null;
    
    const sourceNode = getNode(connectedEdges[0].source);
    if (!sourceNode) return null;
    
    // 从视频任务节点获取生成的视频（使用 videoUrl 字段）
    if (sourceNode.type === 'videoTask') {
      if (sourceNode.data?.videoUrl) {
        console.log('[KlingLipSync] 找到视频任务节点，视频URL:', sourceNode.data.videoUrl);
        return sourceNode.data.videoUrl;
      } else {
        console.log('[KlingLipSync] 找到视频任务节点，但没有视频URL');
      }
    }
    
    // 从视频参考节点获取视频
    if (sourceNode.type === 'videoReference') {
      if (sourceNode.data?.videoUrl) {
        console.log('[KlingLipSync] 找到视频参考节点，视频URL:', sourceNode.data.videoUrl);
        return sourceNode.data.videoUrl;
      } else {
        console.log('[KlingLipSync] 找到视频参考节点，但没有视频URL');
      }
    }
    
    return null;
  }, [edges, getNode, id]);

  // 获取连接的音频节点
  const getConnectedAudioUrl = useCallback(() => {
    // 查找连接到 audio handle 的边，如果没有 targetHandle 则查找所有连接的边
    const connectedEdges = edges.filter(edge => {
      if (edge.target !== id) return false;
      // 如果有 targetHandle，必须是 'audio'；如果没有 targetHandle，则检查源节点类型
      if (edge.targetHandle) {
        return edge.targetHandle === 'audio';
      }
      // 没有 targetHandle 的情况下，接受 audioReference 和 audioTask 类型
      const sourceNode = getNode(edge.source);
      return sourceNode && (sourceNode.type === 'audioReference' || sourceNode.type === 'audioTask');
    });
    
    if (connectedEdges.length === 0) return null;
    
    const sourceNode = getNode(connectedEdges[0].source);
    if (!sourceNode) return null;
    
    // 从音频参考节点获取音频
    if (sourceNode.type === 'audioReference') {
      if (sourceNode.data?.audioUrl) {
        console.log('[KlingLipSync] 找到音频参考节点，音频URL:', sourceNode.data.audioUrl);
        return sourceNode.data.audioUrl;
      } else {
        console.log('[KlingLipSync] 找到音频参考节点，但没有音频URL');
      }
    }
    
    // 从音频任务节点获取生成的音频（使用 audioUrl 字段）
    if (sourceNode.type === 'audioTask') {
      if (sourceNode.data?.audioUrl) {
        console.log('[KlingLipSync] 找到音频任务节点，音频URL:', sourceNode.data.audioUrl);
        return sourceNode.data.audioUrl;
      } else {
        console.log('[KlingLipSync] 找到音频任务节点，但没有音频URL');
      }
    }
    
    return null;
  }, [edges, getNode, id]);

  // 验证连接
  const isValidConnection = useCallback((connection) => {
    const sourceNode = getNode(connection.source);
    if (!sourceNode) return false;
    
    if (connection.targetHandle === 'video') {
      // video handle 允许连接视频任务节点和视频参考节点
      return sourceNode.type === 'videoTask' || sourceNode.type === 'videoReference';
    }
    
    if (connection.targetHandle === 'audio') {
      // audio handle 允许连接音频参考节点和音频任务节点
      return sourceNode.type === 'audioReference' || sourceNode.type === 'audioTask';
    }
    
    // 如果没有 targetHandle，根据源节点类型判断
    if (!connection.targetHandle) {
      return sourceNode.type === 'videoTask' || 
             sourceNode.type === 'videoReference' || 
             sourceNode.type === 'audioReference' ||
             sourceNode.type === 'audioTask';
    }
    
    return true;
  }, [getNode]);

  // 复制节点
  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  // 生成对口型视频
  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      console.log('[KlingLipSync] 开始生成任务');
      console.log('[KlingLipSync] 当前连接的边:', edges.filter(e => e.target === id));
      
      // 1. 获取视频 URL
      const videoUrl = getConnectedVideoUrl();
      console.log('[KlingLipSync] 获取到的视频URL:', videoUrl);
      
      if (!videoUrl) {
        throw new Error('请先连接视频任务节点或视频参考节点，并确保视频已生成');
      }
      
      // 2. 获取音频 URL
      const connectedAudio = getConnectedAudioUrl();
      console.log('[KlingLipSync] 连接的音频URL:', connectedAudio);
      
      if (!connectedAudio) {
        throw new Error('请连接音频节点（音频参考节点或音频任务节点），并确保音频已生成');
      }
      
      // 3. 准备输入参数
      const inputParams = {
        video_url: videoUrl,
        audio_url: connectedAudio,
      };
      
      // 4. 获取用户 token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        throw new Error('请先登录');
      }
      
      // 5. 调用 API 创建任务
      const response = await apiService.request(
        `${apiService.apiBase}/fal/tasks/create`,
        'POST',
        {
          token: userData.token,
          user_type: 1,
          app_name: 'fal-ai/kling-video/lipsync/audio-to-video',
          input_params: inputParams,
        }
      );
      
      if (response.code === 200 && response.data) {
        const taskId = response.data.task_id;
        
        toast.success('对口型任务已创建！');
        
        // 6. 创建任务节点
        const currentNode = getNode(id);
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'videoTask',
          position: {
            x: currentNode.position.x + 450,
            y: currentNode.position.y,
          },
          data: {
            taskId: taskId,
            taskType: 'kling-lip-sync',
            status: 'pending',
            inputParams: inputParams,
            title: '可灵对口型',
          },
        };
        
        // 7. 创建连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };
        
        // 8. 添加节点和边
        await addNodesWithEdges([taskNode], [newEdge]);
        
        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }
      
    } catch (err) {
      console.error('创建对口型任务失败:', err);
      const errorMessage = err?.message || '创建任务失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'klingLipSync', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  return (
    <div className="KlingLipSyncNode-container">
      {/* 复制按钮 */}
      <button 
        className="KlingLipSyncNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 视频输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="video"
        className="KlingLipSyncNode-handle KlingLipSyncNode-handle-video"
        isValidConnection={isValidConnection}
        style={{ top: '40%' }}
      >
        <div 
          className="KlingLipSyncNode-handle-label KlingLipSyncNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'video', 'video')}
          title="点击自动创建视频参考节点"
        >
          视频
        </div>
      </Handle>

      {/* 音频输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="audio"
        className="KlingLipSyncNode-handle KlingLipSyncNode-handle-audio"
        isValidConnection={isValidConnection}
        style={{ top: '60%' }}
      >
        <div 
          className="KlingLipSyncNode-handle-label KlingLipSyncNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'audio', 'audio')}
          title="点击自动创建音频参考节点"
        >
          音频
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="KlingLipSyncNode-handle KlingLipSyncNode-handle-output"
      />

      {/* 标题栏 */}
      <div className="KlingLipSyncNode-title">可灵对口型</div>

      {/* 内容区域 */}
      <div className="KlingLipSyncNode-wrapper">
        {/* 连接状态显示 */}
        <div className="KlingLipSyncNode-status">
          <div className="KlingLipSyncNode-status-item">
            <span className="KlingLipSyncNode-status-label">视频输入：</span>
            <span className={`KlingLipSyncNode-status-value ${getConnectedVideoUrl() ? 'KlingLipSyncNode-status-connected' : 'KlingLipSyncNode-status-disconnected'}`}>
              {getConnectedVideoUrl() ? '✓ 已连接' : '✗ 未连接'}
            </span>
          </div>
          <div className="KlingLipSyncNode-status-item">
            <span className="KlingLipSyncNode-status-label">音频输入：</span>
            <span className={`KlingLipSyncNode-status-value ${getConnectedAudioUrl() ? 'KlingLipSyncNode-status-connected' : 'KlingLipSyncNode-status-disconnected'}`}>
              {getConnectedAudioUrl() ? '✓ 已连接' : '✗ 未连接'}
            </span>
          </div>
        </div>

        {/* 使用说明 */}
        <div className="KlingLipSyncNode-hint">
          💡 <strong>使用说明</strong><br />
          • 连接视频任务节点和音频节点（音频参考节点或音频任务节点）<br />
          • 视频要求：.mp4/.mov, 2-10秒, 720p-1080p<br />
          • 音频要求：.mp3/.wav/.m4a/.aac, &lt; 5MB<br />
          点击创建后会自动生成视频任务节点
        </div>

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly}
          className={`KlingLipSyncNode-generate ${isGenerating || readOnly ? 'KlingLipSyncNode-generate-disabled' : ''}`}
        >
          {isGenerating ? '创建中...' : '创建任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="KlingLipSyncNode-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
});

KlingLipSyncNode.displayName = 'KlingLipSyncNode';

